/**
 * Map a Google sign-in failure to the user-facing message and CTA.
 *
 * Drives the inline error banner under the Google button on the
 * Sign-In screen (see LoginPage.js). Backend codes were defined by the
 * backend dev in SCRUM-61 (Google SSO Flows & Edge Cases, v1.2,
 * 20 May 2026). The browser-side cases come from the same document.
 *
 * Input: anything caught from the Google sign-in path. We accept three
 * shapes:
 *   - axios error from POST /users/auth/google/ (most common)
 *   - thin event object passed by GoogleLogin's onError callback
 *     (the @react-oauth/google SDK invokes onError with no useful
 *     argument, so we treat it as an opaque "Google rejected the
 *     credential or the user dismissed the prompt" signal)
 *   - a plain string error code, useful for tests + manual triggers
 *
 * Output: `{ message, showRetryWithEmailCta }` ready to render, or
 * `null` when the caller should render NOTHING (e.g. the user
 * cancelled the Google popup — per the spec, no message is shown).
 */

// Backend error codes (`response.data.error` on a 4xx from
// /users/auth/google/). Sourced from the backend dev's note attached
// to SCRUM-61. The four "account state" codes get bespoke messages;
// the six "token / oauth validation" codes share a single generic
// message so the user isn't shown internal mechanics.
const ACCOUNT_NOT_ALLOWED = "google_account_not_allowed";
const ACCOUNT_DISABLED = "google_account_disabled";
const ACCOUNT_CONFLICT = "google_account_conflict";
const EMAIL_NOT_VERIFIED = "email_not_verified";

const GENERIC_GOOGLE_FAILURE_CODES = new Set([
  "invalid_id_token",
  "invalid_issuer",
  "invalid_nonce",
  "missing_email",
  "missing_id_token",
  "google_oauth_failed",
]);

// Sentinel returned by callers for non-backend triggers. The
// GoogleSignInButton forwards GSI's onError as a string; this is
// cleaner than synthesising a fake axios error.
export const GSI_ERROR = "__gsi_error__";

// User-facing strings live here so QA can audit the table against the
// SCRUM-61 doc with one search. Strings are intentionally
// hyphen/period punctuation only — no em-dashes (CLAUDE.md rule).
const MESSAGES = {
  accountNotAllowed:
    "We couldn't find a TOCA account for this Google email. Sign in with your TOCA email and password instead.",
  accountDisabled: "Account disabled. Contact your admin.",
  accountConflict:
    "We found more than one TOCA account for this Google email. Contact support.",
  emailNotVerified:
    "Your Google email isn't verified yet. Verify it in your Google account, then try again.",
  genericFailure:
    "Sign-in failed. Try again, or contact support if this keeps happening.",
  popupBlocked:
    "Popup blocked. Allow popups for tocahealth.com and try again.",
  googleUnreachable:
    "Can't reach Google right now. Check your connection and try again.",
  backendDown:
    "Something went wrong on our end. Please try again in a few moments.",
};

const banner = (message, showRetryWithEmailCta = false) => ({
  message,
  showRetryWithEmailCta,
});

// Read the structured error code off an axios error. Mirrors
// `matchesGoogleErrorCode` in services/auth/index.js so the two
// extractors agree. Returns null if nothing usable.
const extractBackendCode = (error) => {
  const data = error?.response?.data;
  if (!data) return null;
  return data.error || data.code || data.detail || null;
};

// Heuristic for "the network never made it out". axios sets
// `error.code` to ERR_NETWORK when DNS / TLS / browser-level fetch
// fails, and to ECONNABORTED when a configured timeout fires. Neither
// case has a `response`. We also consider `navigator.onLine === false`
// since that's the cleanest offline signal.
const isNetworkFailure = (error) => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return true;
  }
  if (!error?.response) {
    const code = error?.code;
    if (code === "ERR_NETWORK" || code === "ECONNABORTED") return true;
    // axios marks aborted requests with message containing "Network".
    if (error?.message && /network/i.test(error.message)) return true;
  }
  return false;
};

const isServerFailure = (error) => {
  const status = error?.response?.status;
  return typeof status === "number" && status >= 500 && status < 600;
};

export const mapGoogleSignInError = (error) => {
  // GSI's onError fires for: user dismissed the FedCM prompt, user
  // closed the popup, browser blocked the popup, Google itself
  // rejected the request (very rare). The SDK does not tell us which.
  //
  // The PDF wants three different messages here (popup blocked / user
  // cancelled / Google unreachable) but the @react-oauth/google SDK
  // gives us no discriminator. We use one heuristic we CAN check:
  //   - if the browser is offline, surface the Google-unreachable
  //     message.
  //   - otherwise return null and render nothing, treating the event
  //     as a user-cancel (the most common case by far).
  //
  // Popup-blocked detection is intentionally not implemented here.
  // The current GoogleLogin button uses Google's iframe + FedCM flow
  // which doesn't open a popup in modern browsers; the case is rarely
  // reachable in practice. Revisit if SDK gains an error-type arg.
  if (error === GSI_ERROR || error?.__gsiError === true) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return banner(MESSAGES.googleUnreachable);
    }
    return null;
  }

  // Plain string convenience (mostly for tests + manual triggers).
  if (typeof error === "string") {
    return mapByBackendCode(error);
  }

  // Network / offline takes precedence over status checks, because
  // axios may still surface an `error` object without `response`.
  if (isNetworkFailure(error)) {
    return banner(MESSAGES.googleUnreachable);
  }

  if (isServerFailure(error)) {
    return banner(MESSAGES.backendDown);
  }

  const code = extractBackendCode(error);
  if (code) {
    return mapByBackendCode(code);
  }

  // Unknown shape -> generic.
  return banner(MESSAGES.genericFailure);
};

const mapByBackendCode = (code) => {
  switch (code) {
    case ACCOUNT_NOT_ALLOWED:
      return banner(MESSAGES.accountNotAllowed, true);
    case ACCOUNT_DISABLED:
      return banner(MESSAGES.accountDisabled);
    case ACCOUNT_CONFLICT:
      return banner(MESSAGES.accountConflict);
    case EMAIL_NOT_VERIFIED:
      return banner(MESSAGES.emailNotVerified);
    default:
      if (GENERIC_GOOGLE_FAILURE_CODES.has(code)) {
        return banner(MESSAGES.genericFailure);
      }
      return banner(MESSAGES.genericFailure);
  }
};

export const __testables__ = { MESSAGES, GENERIC_GOOGLE_FAILURE_CODES };
